// How a recipe reads on its page and in the cooking layout (redesign slice
// 4): ingredient lines with a separate amount column, scaled for the
// servings being cooked; numbered steps; times; and where the recipe sits in
// the plan. Pure functions, tested in tests/recipe-display.test.mjs.
import { DAYS, MEAL_SLOTS, NON_NUMERIC_UNITS } from "./constants";
import { formatQuantity, orderedDays, parseQuantity, round, toISODate } from "./utils";
import { buildGroupedRows } from "./recipeUtils";
import { normalizeDishes } from "./planUtils";
import type { Day, DishType, MealSlot, PlansByWeek, Recipe } from "./types";

export interface Ingredient {
  id: string;
  component?: string;
  type?: "ingredient" | "note";
  name: string;
  prep?: string;
  quantity?: string;
  unit?: string;
}

export interface Instruction {
  id: string;
  component?: string;
  type: "step" | "note";
  text: string;
}

export type IngredientLine =
  | { kind: "header"; key: string; label: string }
  | { kind: "ingredient"; key: string; id: string; amount: string; text: string }
  | { kind: "note"; key: string; text: string };

export type StepLine = { kind: "header"; key: string; label: string } | { kind: "step"; key: string; id: string; number: number; text: string } | { kind: "note"; key: string; text: string };

// --------------------------------------------------------- scaling ---

// What to multiply quantities by to serve `target` instead of the recipe's
// own servings. 1 when either is missing.
export function scaleFactor(recipeServings: number, target: number): number {
  return recipeServings > 0 && target > 0 ? target / recipeServings : 1;
}

const FRACTIONS: Record<string, string> = { "1/4": "¼", "1/3": "⅓", "1/2": "½", "2/3": "⅔", "3/4": "¾" };

// formatQuantity's "1 1/2" as "1½" (board 29). Display only; what's stored
// and what the grocery list adds up are unchanged.
export const prettyFractions = (qty: string) => qty.replace(/^(\d+ )?(1\/4|1\/3|1\/2|2\/3|3\/4)$/, (_, whole: string | undefined, frac: string) => `${(whole || "").trim()}${FRACTIONS[frac]}`);

// "1½ lb", "3", "to taste", "" -- the amount column. A quantity that
// doesn't parse (blank, "a few") is shown as written, unscaled.
export function formatAmount(ing: Ingredient, factor = 1): string {
  const unit = ing.unit || "";
  if (NON_NUMERIC_UNITS.includes(unit)) return unit;
  const raw = (ing.quantity ?? "").trim();
  const n = parseQuantity(raw);
  const qty = n > 0 ? prettyFractions(String(formatQuantity(round(n * factor, 3)))) : raw;
  return [qty, unit].filter(Boolean).join(" ");
}

export function ingredientLines(ingredients: Ingredient[], factor = 1): IngredientLine[] {
  return (buildGroupedRows(ingredients) as { kind: string; key: string; label?: string; item?: Ingredient }[]).map((row) => {
    if (row.kind === "header") return { kind: "header", key: row.key, label: row.label || "" };
    const ing = row.item as Ingredient;
    if (ing.type === "note") return { kind: "note", key: row.key, text: ing.name };
    return { kind: "ingredient", key: row.key, id: ing.id, amount: formatAmount(ing, factor), text: [ing.name, ing.prep].filter(Boolean).join(", ") };
  });
}

// Steps are numbered across the whole recipe; notes and component headers
// don't take a number.
export function stepLines(instructions: Instruction[]): StepLine[] {
  let n = 0;
  return (buildGroupedRows(instructions) as { kind: string; key: string; label?: string; item?: Instruction }[]).map((row) => {
    if (row.kind === "header") return { kind: "header", key: row.key, label: row.label || "" };
    const ins = row.item as Instruction;
    if (ins.type === "note") return { kind: "note", key: row.key, text: ins.text };
    n += 1;
    return { kind: "step", key: row.key, id: ins.id, number: n, text: ins.text };
  });
}

// ----------------------------------------------------------- times ---

const mins = (n: number) => (n >= 60 ? `${Math.floor(n / 60)} h${n % 60 ? ` ${n % 60} min` : ""}` : `${n} min`);

// ["15 min active", "30 min cook", "45 min"] -- the last is the total. A
// zero part is left out; an untimed recipe gives [].
export function timeParts(recipe: Pick<Recipe, "activeTime" | "cookTime">): { parts: string[]; total: string } {
  const active = recipe.activeTime || 0;
  const cook = recipe.cookTime || 0;
  const parts = [active ? `${mins(active)} active` : "", cook ? `${mins(cook)} cook` : ""].filter(Boolean);
  return { parts, total: active + cook ? mins(active + cook) : "" };
}

// ----------------------------------------------------- in the plan ---

export interface Placement {
  weekStart: string;
  day: Day;
  slot: MealSlot;
  dishType: DishType;
  date: Date;
  label: string; // "Tonight's dinner", "Tomorrow's lunch", "Wed, Oct 7 · dinner"
}

// Where the recipe is planned from today on, soonest first. Weekly lists
// (snacks, preserves, medicinal) have no day, so they aren't placements.
export function upcomingPlacements(plansByWeek: PlansByWeek, recipeId: string, weekStartDay: Day, now: Date): Placement[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayISO = toISODate(today);
  const tomorrowISO = toISODate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  const order = orderedDays(weekStartDay) as readonly Day[];
  const found: Placement[] = [];
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    const start = new Date(`${weekStart}T00:00:00`);
    for (const day of DAYS) {
      const date = new Date(start);
      date.setDate(start.getDate() + order.indexOf(day));
      const iso = toISODate(date);
      if (iso < todayISO) continue;
      for (const slot of MEAL_SLOTS) {
        for (const [dishType, id] of Object.entries(normalizeDishes(week.days?.[day]?.[slot]) as Record<string, string>)) {
          if (id !== recipeId) continue;
          const label =
            iso === todayISO ? (slot === "dinner" ? "Tonight's dinner" : `Today's ${slot}`) : iso === tomorrowISO ? `Tomorrow's ${slot}` : `${date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${slot}`;
          found.push({ weekStart, day, slot, dishType: dishType as DishType, date, label });
        }
      }
    }
  }
  return found.sort((a, b) => a.date.getTime() - b.date.getTime() || MEAL_SLOTS.indexOf(a.slot) - MEAL_SLOTS.indexOf(b.slot));
}

// ------------------------------------------------- a meal's dishes ---

export interface MealDish {
  dishType: DishType;
  recipeId: string;
}

// The real recipes planned together in one meal (for the cooking layout's
// dish switcher), main first.
export function mealDishes(plansByWeek: PlansByWeek, weekStart: string, day: Day, slot: MealSlot, isRecipe: (id: string) => boolean): MealDish[] {
  const dishes = normalizeDishes(plansByWeek[weekStart]?.days?.[day]?.[slot]) as Partial<Record<DishType, string>>;
  return (Object.entries(dishes) as [DishType, string][]).filter(([, id]) => id && isRecipe(id)).map(([dishType, recipeId]) => ({ dishType, recipeId }));
}
